import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardDeadline, AgendaBucket } from '../../types';
import DeadlineCountdown from './DeadlineCountdown';
import { deadlineDate, byDueDate } from '../../utils/deadlineDates';

function EditableDate({ value, onSave }: { value: string | null; onSave: (d: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value?.slice(0, 10) || '');
  if (!editing) {
    return (
      <button
        onClick={() => { setDraft(value?.slice(0, 10) || ''); setEditing(true); }}
        className="text-xs tabular-nums text-gray-500 hover:text-primary-500"
        title="Edit date"
      >
        {deadlineDate(value) ?? 'Set date'}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="rounded-lg border-gray-300 px-1.5 py-0.5 text-xs focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25" />
      <button onClick={() => { if (draft) onSave(draft); setEditing(false); }} className="text-xs font-semibold text-primary-500 hover:underline underline-offset-[3px]">Save</button>
      <button onClick={() => setEditing(false)} className="text-xs text-gray-400">Cancel</button>
    </span>
  );
}

const GROUPS: { bucket: AgendaBucket; label: string; cls: string }[] = [
  { bucket: 'overdue', label: 'Overdue', cls: 'text-primary-500' },
  { bucket: 'today', label: 'Today', cls: 'text-primary-500' },
  { bucket: 'this_week', label: 'This week', cls: 'text-warning' },
  { bucket: 'later', label: 'Later', cls: 'text-gray-400' },
  { bucket: 'undated', label: 'Needs a date', cls: 'text-gray-400' },
];

interface Props {
  deadlines: DashboardDeadline[];
  onToggle: (d: DashboardDeadline) => void;
  onReschedule: (d: DashboardDeadline, due_date: string) => void;
  onOpenDocs: (d: DashboardDeadline) => void;
}

export default function DeadlineAgenda({ deadlines, onToggle, onReschedule, onOpenDocs }: Props) {
  const navigate = useNavigate();
  const open = deadlines.filter((d) => !d.is_completed);
  if (open.length === 0) {
    return <div className="py-10 text-center text-[12.5px] text-gray-500">No open deadlines. All clear.</div>;
  }
  return (
    <div className="space-y-5">
      {GROUPS.map((g) => {
        const rows = open.filter((d) => d.bucket === g.bucket).sort(byDueDate);
        if (rows.length === 0) return null;
        return (
          <div key={g.bucket}>
            <div className={`mb-2 text-[9.5px] font-bold uppercase tracking-[.13em] ${g.cls}`}>{g.label} · {rows.length}</div>
            <div className="space-y-2">
              {rows.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 transition-colors hover:border-gray-400">
                  <input type="checkbox" checked={d.is_completed} onChange={() => onToggle(d)} className="rounded-sm border-gray-300 text-success focus:ring-success/25" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-[13px] font-medium text-gray-800">{d.label}</p>
                    <button onClick={() => navigate(`/crm/deals/${d.deal_id}`)} className="text-[11.5px] font-medium text-primary-500 hover:underline underline-offset-[3px]">
                      {d.deal_name}
                    </button>
                  </div>
                  <EditableDate value={d.due_date} onSave={(date) => onReschedule(d, date)} />
                  <DeadlineCountdown dueDate={d.due_date} completed={d.is_completed} />
                  <button
                    onClick={() => onOpenDocs(d)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-500"
                    title="Documents"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                    </svg>
                    {d.document_count > 0 ? d.document_count : ''}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
